import { useEffect, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { Toaster as Sonner } from "sonner";
import useThemeStore from "@/stores/theme";
import { ROUTES } from "@/config/routes";
import { ProtectedRoute } from "@/routing/ProtectedRoute";
import { PublicRoute } from "@/routing/PublicRoute";
import { TokenRefreshProvider } from "@/components/providers/TokenRefreshProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { lazyWithRetry, lazyWithRetryNamed } from "@/utils/lazyWithRetry";

// Lazy load pages with retry logic
const Dashboard = lazyWithRetryNamed(
  () => import("@/pages/Dashboard"),
  "Dashboard",
  "Dashboard"
);
const LoginPage = lazyWithRetryNamed(
  () => import("@/pages/LoginPage"),
  "LoginPage",
  "LoginPage"
);
const ForgotPasswordPage = lazyWithRetryNamed(
  () => import("@/pages/ForgotPasswordPage"),
  "ForgotPasswordPage",
  "ForgotPasswordPage"
);
const SetPasswordPage = lazyWithRetryNamed(
  () => import("@/pages/SetPasswordPage"),
  "SetPasswordPage",
  "SetPasswordPage"
);
const AcceptInvitationPage = lazyWithRetryNamed(
  () => import("@/pages/AcceptInvitationPage"),
  "AcceptInvitationPage",
  "AcceptInvitationPage"
);
const TeamSelectionPage = lazyWithRetryNamed(
  () => import("@/pages/TeamSelectionPage"),
  "TeamSelectionPage",
  "TeamSelectionPage"
);
const TeamsPage = lazyWithRetryNamed(
  () => import("@/pages/TeamsPage"),
  "TeamsPage",
  "TeamsPage"
);
const TeamDetailsPage = lazyWithRetryNamed(
  () => import("@/pages/TeamDetailsPage"),
  "TeamDetailsPage",
  "TeamDetailsPage"
);
const CampaignsPage = lazyWithRetryNamed(
  () => import("@/pages/CampaignsPage"),
  "CampaignsPage",
  "CampaignsPage"
);
const CampaignCreatePage = lazyWithRetryNamed(
  () => import("@/pages/CampaignCreatePage"),
  "CampaignCreatePage",
  "CampaignCreatePage"
);
const AgentsPage = lazyWithRetryNamed(
  () => import("@/pages/AgentsPage"),
  "AgentsPage",
  "AgentsPage"
);
const WorkflowBuilderPage = lazyWithRetry(
  () => import("@/pages/WorkflowBuilderPage"),
  "WorkflowBuilderPage"
);
const TemplateSelectionPage = lazyWithRetry(
  () => import("@/pages/TemplateSelectionPage"),
  "TemplateSelectionPage"
);
const MermaidDiagramPage = lazyWithRetry(
  () => import("@/pages/MermaidDiagramPage"),
  "MermaidDiagramPage"
);
const ChannelsPage = lazyWithRetryNamed(
  () => import("@/pages/ChannelsPage"),
  "ChannelsPage",
  "ChannelsPage"
);
const WebChatWidgetsPage = lazyWithRetryNamed(
  () => import("@/pages/WebChatWidgetsPage"),
  "WebChatWidgetsPage",
  "WebChatWidgetsPage"
);
const WidgetCreatorPage = lazyWithRetryNamed(
  () => import("@/pages/WidgetCreatorPage"),
  "WidgetCreatorPage",
  "WidgetCreatorPage"
);
const ChannelDetailsPage = lazyWithRetryNamed(
  () => import("@/pages/ChannelDetailsPage"),
  "ChannelDetailsPage",
  "ChannelDetailsPage"
);
const EmailConfigPage = lazyWithRetryNamed(
  () => import("@/pages/EmailConfigPage"),
  "EmailConfigPage",
  "EmailConfigPage"
);
const PersonalProfilePage = lazyWithRetryNamed(
  () => import("@/pages/PersonalProfilePage"),
  "PersonalProfilePage",
  "PersonalProfilePage"
);
const ComingSoonPage = lazyWithRetryNamed(
  () => import("@/pages/ComingSoonPage"),
  "ComingSoonPage",
  "ComingSoonPage"
);
const LibraryPage = lazyWithRetryNamed(
  () => import("@/pages/LibraryPage"),
  "LibraryPage",
  "LibraryPage"
);
const InboxPage = lazyWithRetryNamed(
  () => import("@/pages/InboxPage"),
  "InboxPage",
  "InboxPage"
);
const FreeflowAgentPlaygroundPage = lazyWithRetryNamed(
  () => import("@/pages/FreeflowAgentPlaygroundPage"),
  "FreeflowAgentPlaygroundPage",
  "FreeflowAgentPlaygroundPage"
);
const WidgetTestPlaygroundPage = lazyWithRetryNamed(
  () => import("@/pages/WidgetTestPlaygroundPage"),
  "WidgetTestPlaygroundPage",
  "WidgetTestPlaygroundPage"
);
const OAuthCallbackPage = lazyWithRetryNamed(
  () => import("@/pages/OAuthCallbackPage"),
  "OAuthCallbackPage",
  "OAuthCallbackPage"
);
const TemplatesPage = lazyWithRetryNamed(
  () => import("@/pages/TemplatesPage"),
  "TemplatesPage",
  "TemplatesPage"
);
const ContactsPage = lazyWithRetryNamed(
  () => import("@/pages/ContactsPage"),
  "ContactsPage",
  "ContactsPage"
);
const ContactsViewPage = lazyWithRetryNamed(
  () => import("@/pages/ContactsViewPage"),
  "ContactsViewPage",
  "ContactsViewPage"
);
const ForbiddenPage = lazyWithRetryNamed(
  () => import("@/pages/ForbiddenPage"),
  "ForbiddenPage",
  "ForbiddenPage"
);

import { PageLoading } from "@/components/ui/loading-spinner";
import { UpdateNotification } from "@/components/UpdateNotification";
import { BillingPage } from "./pages/BillingPage";
import { FloatingDebugTool } from "@/components/debug";

function AppContent() {
  return (
    <ErrorBoundary>
      <UpdateNotification />
      <Suspense fallback={<PageLoading />}>
        <Routes>
        {/* Public Routes */}
        <Route
          path={ROUTES.LOGIN}
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.FORGOT_PASSWORD}
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.RESET_PASSWORD}
          element={
            <PublicRoute redirectIfAuthenticated={false}>
              <SetPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path={ROUTES.ACCEPT_INVITATION}
          element={
            <PublicRoute redirectIfAuthenticated={false}>
              <AcceptInvitationPage />
            </PublicRoute>
          }
        />
        
        {/* OAuth Callback Route */}
        <Route
          path="/auth/callback/:provider"
          element={<OAuthCallbackPage />}
        />

        {/* Team Selection Route - Requires auth but not team context */}
        <Route
          path={ROUTES.SELECT_TEAM}
          element={
            <ProtectedRoute skipTeamContext>
              <TeamSelectionPage />
            </ProtectedRoute>
          }
        />

        {/* Forbidden Page - Requires auth but not team context */}
        <Route
          path={ROUTES.FORBIDDEN}
          element={
            <ProtectedRoute skipTeamContext>
              <ForbiddenPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Require auth and team context */}
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEAMS}
          element={
            <ProtectedRoute skipTeamContext>
              <TeamsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEAM_DETAILS}
          element={
            <ProtectedRoute skipTeamContext>
              <TeamDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEMPLATES}
          element={
            <ProtectedRoute>
              <TemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CAMPAIGNS}
          element={
            <ProtectedRoute>
              <CampaignsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/campaigns/new"
          element={
            <ProtectedRoute>
              <CampaignCreatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.AGENTS}
          element={
            <ProtectedRoute>
              <AgentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.WORKFLOW_BUILDER}
          element={
            <ProtectedRoute>
              <WorkflowBuilderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.WORKFLOW_TEMPLATES}
          element={
            <ProtectedRoute>
              <TemplateSelectionPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.MERMAID_DIAGRAM}
          element={
            <ProtectedRoute>
              <MermaidDiagramPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.FREEFLOW_AGENT_PLAYGROUND}
          element={
            <ProtectedRoute>
              <FreeflowAgentPlaygroundPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.WIDGET_TEST_PLAYGROUND}
          element={
            <ProtectedRoute>
              <WidgetTestPlaygroundPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={`${ROUTES.WORKFLOW_BUILDER}/:agentId`}
          element={
            <ProtectedRoute>
              <WorkflowBuilderPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CHANNELS}
          element={
            <ProtectedRoute>
              <ChannelsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/channels/web-chat-widgets"
          element={
            <ProtectedRoute>
              <WebChatWidgetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/widget/create"
          element={
            <ProtectedRoute>
              <WidgetCreatorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/widget/edit/:widgetId"
          element={
            <ProtectedRoute>
              <WidgetCreatorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/widget/view/:widgetId"
          element={
            <ProtectedRoute>
              <WidgetCreatorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CHANNEL_DETAILS}
          element={
            <ProtectedRoute>
              <ChannelDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/channels/email/:provider"
          element={
            <ProtectedRoute>
              <EmailConfigPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.BILLING}
          element={
            <ProtectedRoute>
              <BillingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.PROFILE}
          element={
            <ProtectedRoute>
              <PersonalProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.CONTACTS}
          element={
            <ProtectedRoute>
              <ContactsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/contacts/:groupId"
          element={
            <ProtectedRoute>
              <ContactsViewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.INBOX}
          element={
            <ProtectedRoute>
              <InboxPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.ANALYTICS}
          element={
            <ProtectedRoute>
              <ComingSoonPage 
                pageName="Analytics" 
                description="Get detailed insights and performance metrics for your campaigns and agents."
              />
            </ProtectedRoute>
          }
        />
        <Route
          path={ROUTES.LIBRARY}
          element={
            <ProtectedRoute>
              <LibraryPage />
            </ProtectedRoute>
          }
        />

        {/* Fallback redirect */}
        <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  const applyTheme = useThemeStore((state) => state.applyTheme);

  // Apply theme on mount
  useEffect(() => {
    applyTheme();
  }, [applyTheme]);

  return (
    <>
      <TokenRefreshProvider>
        <AppContent />
      </TokenRefreshProvider>
      <FloatingDebugTool />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "hsl(var(--background))",
            color: "hsl(var(--foreground))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "12px",
            boxShadow:
              "0 10px 40px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05)",
            backdropFilter: "blur(12px)",
            fontSize: "14px",
            fontWeight: "500",
            padding: "16px 20px",
            maxWidth: "400px",
          },
          success: {
            style: {
              background:
                "linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)",
              borderColor: "rgba(34, 197, 94, 0.3)",
              color: "hsl(var(--foreground))",
            },
            iconTheme: {
              primary: "#22c55e",
              secondary: "hsl(var(--background))",
            },
          },
          error: {
            style: {
              background:
                "linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)",
              borderColor: "rgba(239, 68, 68, 0.3)",
              color: "hsl(var(--foreground))",
            },
            iconTheme: {
              primary: "#ef4444",
              secondary: "hsl(var(--background))",
            },
          },
          loading: {
            style: {
              background:
                "linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)",
              borderColor: "rgba(139, 92, 246, 0.3)",
              color: "hsl(var(--foreground))",
            },
            iconTheme: {
              primary: "#8b5cf6",
              secondary: "hsl(var(--background))",
            },
          },
        }}
      />
      <Sonner 
        position="bottom-right"
        toastOptions={{
          className: "glassmorphism",
        }}
      />
    </>
  );
}

export default App;
